import { storeToRefs } from 'pinia'
import { ModuleAccessEnum, type Workspace } from '@apiTypes'
import { PackageQuotaFlag } from '@/consts/module-quotas'
import { useAuthStore } from '@/stores/auth'

function isPackageFlagEnabledForWorkspace(flag: PackageQuotaFlag, workspace?: Workspace | null): boolean {
  const currentWorkspace = workspace ?? useAuthStore().currentWorkspace

  return currentWorkspace?.packageData?.[flag] ?? false
}

export function isTenderEnabledForWorkspace(workspace?: Workspace | null): boolean {
  return isPackageFlagEnabledForWorkspace(PackageQuotaFlag.Tender, workspace)
}

export function isContractMatchingEnabledForWorkspace(workspace?: Workspace | null): boolean {
  return isPackageFlagEnabledForWorkspace(PackageQuotaFlag.ContractMatching, workspace)
}

type ModuleAccessKey = keyof NonNullable<Workspace['moduleAccess']>

function moduleAccessForWorkspace(module: ModuleAccessKey, workspace?: Workspace | null): ModuleAccessEnum {
  const currentWorkspace = workspace ?? useAuthStore().currentWorkspace

  return currentWorkspace?.moduleAccess?.[module] ?? ModuleAccessEnum.None
}

export function canCreateTimeSlotsForWorkspace(workspace?: Workspace | null): boolean {
  return moduleAccessForWorkspace('timeSlots', workspace) === ModuleAccessEnum.Full
}

// Without the quota a workspace keeps working with the bookings it already has.
export function canUseTimeSlotsHistoryForWorkspace(workspace?: Workspace | null): boolean {
  return moduleAccessForWorkspace('timeSlots', workspace) !== ModuleAccessEnum.None
}

export function isContractAccessEnabledForWorkspace(workspace?: Workspace | null): boolean {
  return isTenderEnabledForWorkspace(workspace) || isContractMatchingEnabledForWorkspace(workspace)
}

export function isVehicleTypesEnabledForWorkspace(workspace?: Workspace | null): boolean {
  return isTenderEnabledForWorkspace(workspace) || canCreateTimeSlotsForWorkspace(workspace)
}

export function useModuleQuotas() {
  const { currentWorkspace } = storeToRefs(useAuthStore())

  const isTenderEnabled = computed(() => isTenderEnabledForWorkspace(currentWorkspace.value))
  const isContractAccessEnabled = computed(() => isContractAccessEnabledForWorkspace(currentWorkspace.value))
  const isVehicleTypesEnabled = computed(() => isVehicleTypesEnabledForWorkspace(currentWorkspace.value))
  const canCreateTimeSlots = computed(() => canCreateTimeSlotsForWorkspace(currentWorkspace.value))
  const canUseTimeSlotsHistory = computed(() => canUseTimeSlotsHistoryForWorkspace(currentWorkspace.value))

  return {
    isTenderEnabled,
    isContractAccessEnabled,
    isVehicleTypesEnabled,
    canCreateTimeSlots,
    canUseTimeSlotsHistory,
  }
}
